import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Section, SectionTitle, Reveal } from "@/components/Section";
import { useContent } from "@/i18n";
import { Shape3D } from "@/components/Objects3D";

const MetaItem = ({ label, value }) => (
  <div>
    <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-cobalt">{label}</p>
    <p className="mt-2 text-sm leading-relaxed text-cream/70">{value}</p>
  </div>
);

export const Projects = () => {
  const [openIdx, setOpenIdx] = useState(0);
  const { c, ui } = useContent();

  return (
    <Section id="research" num="05" chapter={ui.research.chapter} dark>
      <Shape3D shape="sphere" size={64} blue className="absolute right-[5%] top-16 opacity-90 hidden md:block" mouse />
      <Shape3D shape="star" size={44} className="absolute left-[4%] bottom-20 opacity-70 hidden md:block" duration={8} />
      <div className="flex flex-wrap items-end justify-between gap-6">
        <SectionTitle light className="max-w-3xl">
          {ui.research.titleA} <span className="italic text-cobalt">{ui.research.titleB}</span>
        </SectionTitle>
        <Reveal delay={0.15}>
          <p className="max-w-sm text-sm leading-relaxed text-cream/60">{ui.research.intro}</p>
        </Reveal>
      </div>

      <div className="mt-16 border-t border-cream/15">
        {c.projects.map((p, i) => {
          const open = openIdx === i;
          return (
            <Reveal key={p.num} delay={0.04 * i}>
              <article className="border-b border-cream/15" data-testid={`project-item-${i}`}>
                <button
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full flex items-center gap-6 md:gap-10 py-8 md:py-10 text-left group"
                  data-testid={`project-toggle-${i}`}
                >
                  <span className="font-serif italic text-2xl md:text-3xl text-cobalt/90 shrink-0">{p.num}</span>
                  <span className="flex-1">
                    <span className={`block font-serif text-2xl md:text-4xl tracking-tight leading-tight transition-colors duration-400 ${open ? "text-cobalt" : "group-hover:text-cobalt"}`}>
                      {p.title}
                    </span>
                    <span className="mt-2 block text-xs tracking-[0.22em] uppercase text-cream/50">{p.kind}</span>
                  </span>
                  <motion.span animate={{ rotate: open ? 45 : 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="shrink-0 text-cream/60">
                    <Plus size={22} strokeWidth={1.5} />
                  </motion.span>
                </button>

                <AnimatePresence initial={false}>
                  {open && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden"
                    >
                      <div className="pb-12 md:pl-[4.5rem] grid lg:grid-cols-2 gap-10">
                        <div>
                          <p className="text-base md:text-lg leading-relaxed text-cream/85 font-light">{p.overview}</p>
                          <div className="mt-6 flex flex-wrap gap-2">
                            {p.tools.map((t) => (
                              <span key={t} className="rounded-full border border-cream/25 px-3 py-1 text-xs text-cream/70">
                                {t}
                              </span>
                            ))}
                          </div>
                          <div className="mt-8 border-l-2 border-cobalt pl-5">
                            <p className="text-[10px] tracking-[0.28em] uppercase font-bold text-cobalt">{ui.research.keyLesson}</p>
                            <p className="mt-2 font-serif italic text-lg text-cream/90">“{p.lesson}”</p>
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-6 content-start">
                          <MetaItem label={ui.research.objective} value={p.objective} />
                          <MetaItem label={ui.research.methodology} value={p.methodology} />
                          <MetaItem label={ui.research.outcome} value={p.outcome} />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </article>
            </Reveal>
          );
        })}
      </div>
    </Section>
  );
};
